import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReopenCase, ReopenCaseForm, StiiraError } from '@core/models';
import { ReopenCasePost } from '@core/models/leave-admin/reopen-case-post.model';
import { SnackbarService, NotificationService, ErrorService, LayoutService } from '@core/services';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { EmailResult } from '@core/enums';

@Component({
    selector: 'app-reopen-case',
    templateUrl: './reopen-case.component.html',
    styleUrls: ['./reopen-case.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReopenCaseComponent implements OnInit {
    static readonly componentName = 'ReopenCaseComponent';
    
    @Output() isEditing = new EventEmitter<boolean>();
    @Output() createNew = new EventEmitter<number>();

    public form: FormGroup<ReopenCaseForm>;
    public reopenCasePost: ReopenCasePost;
    public isSubmitting$: Subject<boolean>;

    public position = position;
    public onDragEnded = onDragEnded;
  
    private  formInitValues: any;
    private destroy$: Subject<void> = new Subject<void>();

    get noChanges(): boolean {
      return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
    }
  
    get formIsValid(): boolean {
      return this.form.valid;
    }
  
    get sendNotification(): FormControl {
      return this.form.controls.sendNotification as FormControl;
    }

    public isMobile$: Observable<boolean>;
    
    constructor(
      @Inject(MAT_DIALOG_DATA)
      public data: { 
        rcdata: ReopenCase; 
        sysText: any 
      },
      private reopenCaseDialogComponent: MatDialogRef<ReopenCaseComponent>,
      private service: LeaveAdminService,
      private dialog: MatDialog,
      private fb: FormBuilder,
      private snackbar: SnackbarService,
      private notificationService: NotificationService,
      private errorService: ErrorService,
      private layoutService: LayoutService
      ) { 
      this.isSubmitting$ = new Subject();
      this.form = this.fb.group<ReopenCaseForm>({
        caseId: this.fb.control(this.data.rcdata.caseId),
        sendNotification: this.fb.control(false)
      });
    }
  
    ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.reopenCaseDialogComponent, this.isSubmitting$, () => this.cancel());

      this.formInitValues = { ...this.form.value };
    }

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
    }
  
    public reopenCase(): void {
      this.reopenCasePost = {
        caseId: this.data.rcdata.caseId,
        sendNotification: this.data.rcdata.caseHasEmployee && this.data.rcdata.showNotificationToggle
          ? this.sendNotification.value
          : null
        };

      this.service.postReopenCase(this.reopenCasePost)
        .pipe(
          indicate(this.isSubmitting$),
          finalize(() => { }))
        .subscribe(
          (res) => {
            this.reopenCaseDialogComponent.close(res.leaveCaseDetails);

            if (res.emailResult == EmailResult.success) {
              let snackText =  this.data.sysText.reopenCase.caseReopenedWithEmailSuccess;
              this.snackbar.open(snackText, this.data.sysText.reopenCase.dismiss);
            }
            else if (res.emailResult == EmailResult.successWithFailure) {
              let toastText = this.data.sysText.reopenCase.caseReopenedWithEmailSuccessFailure;
              this.notificationService.showErrorToast(toastText);
            }
            else if (res.emailResult == EmailResult.failure) {
              let toastText = this.data.sysText.reopenCase.caseReopenedWithEmailFailure;
              this.notificationService.showErrorToast(toastText);
            }
            else {
              let snackText = this.data.sysText.reopenCase.caseReopenedWithoutEmail;
              this.snackbar.open(snackText, this.data.sysText.reopenCase.dismiss);
            }
          },
          (err: StiiraError) => 
            this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
    }
  
    public cancel(): void {
      if (this.noChanges)
        this.reopenCaseDialogComponent.close();
      else
        this.openUnsavedChangesDialog();
    }

    private openUnsavedChangesDialog(): void {
      const dialogConfig: MatDialogConfig = {
        id: UnsavedChangesComponent.componentName,
        width: '300px',
        data: this.data.sysText.leavePage
      };
      
      this.dialog.open(UnsavedChangesComponent, dialogConfig)
        .afterClosed()
        .subscribe(
          (res: boolean) => {
            if (res)
              this.reopenCaseDialogComponent.close();
            });
    }
  }
